import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { MapPin, BarChart2, Zap } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import ParticleTerrainCanvas from './ParticleTerrainCanvas';

const STEP_ICONS = [MapPin, BarChart2, Zap];

export default function SolutionSection() {
  const { t } = useLanguage();
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });
  const steps = t('solution.steps');

  return (
    <section
      id="solution"
      className="relative pt-20 pb-10 sm:pt-28 sm:pb-14 px-6 sm:px-12 lg:px-24 overflow-hidden"
      data-testid="solution-section"
    >
      {/* ── Particle terrain — anchored to section right edge, starts behind cards ── */}
      <div className="absolute right-0 inset-y-0 w-[62%] pointer-events-none z-0">
        {/* Gradient fade on the left — softer, longer blend */}
        <div
          className="absolute inset-y-0 left-0 w-64 z-10 pointer-events-none"
          style={{ background: 'linear-gradient(to right, #0A0D0A 0%, #0A0D0A 25%, transparent 100%)' }}
        />
        {/* Subtle top fade */}
        <div
          className="absolute top-0 inset-x-0 h-24 z-10 pointer-events-none"
          style={{ background: 'linear-gradient(to bottom, #0A0D0A 0%, transparent 100%)' }}
        />
        {/* Subtle bottom fade */}
        <div
          className="absolute bottom-0 inset-x-0 h-24 z-10 pointer-events-none"
          style={{ background: 'linear-gradient(to top, #0A0D0A 0%, transparent 100%)' }}
        />
        <ParticleTerrainCanvas className="w-full h-full" />
      </div>

      {/* ── Content ── */}
      <div className="max-w-6xl mx-auto relative z-10">

        {/* Header */}
        <motion.div
          ref={sectionRef}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="mb-14"
        >
          <h2 className="text-3xl sm:text-4xl tracking-tight font-medium max-w-3xl leading-snug">
            {t('solution.heading')}{' '}
            <span className="text-[#BDD6B5]">{t('solution.headingGreen')}</span>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-white/78 max-w-2xl leading-relaxed">
            {t('solution.subtitle')}
          </p>
        </motion.div>

        {/* Step cards — wider now, terrain still visible on the right */}
        <div className="max-w-[780px] flex flex-col gap-5">
          {Array.isArray(steps) && steps.map((step, i) => {
            const Icon = STEP_ICONS[i];
            const StepCard = () => {
              const ref = useRef(null);
              const inView = useInView(ref, { once: true, margin: '-40px' });
              return (
                <motion.div
                  ref={ref}
                  initial={{ opacity: 0, y: 28 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: i * 0.14 }}
                  data-testid={`how-step-${i}`}
                >
                  <div className="glass-card p-7 sm:p-8 flex gap-5 items-start group relative overflow-hidden">
                    {/* Animated top accent on hover */}
                    <div className="absolute top-0 left-0 right-0 h-[1.5px] bg-gradient-to-r from-transparent via-[#BDD6B5]/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                    {/* Step number + icon */}
                    <div className="shrink-0 flex flex-col items-center gap-1.5 mt-0.5">
                      <span className="font-mono text-2xl font-light text-[#BDD6B5]/65 leading-none">
                        {step.num}
                      </span>
                      <Icon size={20} className="text-[#BDD6B5]" strokeWidth={1.5} />
                    </div>

                    {/* Text */}
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-2">{step.label}</h3>
                      <p className="text-sm text-white/78 leading-relaxed">{step.desc}</p>
                    </div>
                  </div>
                </motion.div>
              );
            };
            return <StepCard key={i} />;
          })}
        </div>

      </div>
    </section>
  );
}
